import { useMemo } from "react";
import { useGetHashKeys } from "@/features/auth/api/hash.queries";
import type {
  HashKeyApiResponse,
  UIHashKey,
  SortOption,
} from "@/features/auth/types/hashKeys.types";

/**
 * Custom hook for managing hash keys data transformation and filtering
 */
export const useHashKeysData = (
  searchQuery: string,
  sortBy: SortOption,
  filterRole: string = "all", // Add this parameter
) => {
  const { data: hashKeysResponse, isLoading, error } = useGetHashKeys();

  // Transform API data for UI components
  const rawHashKeys = useMemo(() => {
    if (!hashKeysResponse?.hash_keys) return [];

    return hashKeysResponse.hash_keys.map(
      (hashKey: HashKeyApiResponse): UIHashKey => ({
        id: hashKey.hash_key_id,
        name: hashKey.hash_key_name,
        dateCreated: hashKey.created_at,
        lastUsed: hashKey.last_used_at,
        hashKey: hashKey.hash_key_preview,
        active: hashKey.active,
        usageCount: hashKey.usage_count,
        apiKeysCount: hashKey.api_keys_count,
      }),
    );
  }, [hashKeysResponse]);

  // Get status counts for filter
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rawHashKeys.forEach((hashKey: UIHashKey) => {
      const status = hashKey.active ? "Active" : "Inactive";
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [rawHashKeys]);

  // Apply filtering, sorting, and search
  const filteredHashKeys = useMemo(() => {
    let filtered = [...rawHashKeys];

    // Apply status filter (ADD THIS SECTION)
    if (filterRole !== "all") {
      filtered = filtered.filter((hashKey: UIHashKey) => {
        const status = hashKey.active ? "Active" : "Inactive";
        return status === filterRole;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (hashKey: UIHashKey) =>
          hashKey.name.toLowerCase().includes(query) ||
          hashKey.hashKey.toLowerCase().includes(query),
      );
    }

    // Apply sorting
    filtered.sort((a: UIHashKey, b: UIHashKey) => {
      switch (sortBy) {
        case "alphabetical_asc":
          return a.name.localeCompare(b.name);
        case "alphabetical_desc":
          return b.name.localeCompare(a.name);
        case "newest_first":
          return (
            new Date(b.dateCreated).getTime() -
            new Date(a.dateCreated).getTime()
          );
        case "oldest_first":
          return (
            new Date(a.dateCreated).getTime() -
            new Date(b.dateCreated).getTime()
          );
        default:
          return 0;
      }
    });

    return filtered;
  }, [rawHashKeys, searchQuery, sortBy, filterRole]); // Add filterRole to dependencies

  return {
    hashKeys: filteredHashKeys,
    rawHashKeys,
    statusCounts,
    isLoading,
    error,
  };
};
